import {ChangeEvent, ReactElement, useEffect, useState} from "react";
import {
  Button, Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, InputAdornment, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tab, Tabs, Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import ChecklistIcon from '@mui/icons-material/Checklist';
import {getPalette} from "../../../configuration/configuration";
import IconButton from "@mui/material/IconButton";
import {useFetch} from "../../../system/useFetch";
import {IState} from "../../../types";
import {ISteelGradeItem} from "./types";
import {useSelector} from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import ClearIcon from '@mui/icons-material/Clear';

interface IProps {
  grades: string[];
  onAccept: (grades: string[]) => void;
}

const statusColor = getPalette().statusColor.blueGray;

const SteelSelector = ({ onAccept, grades }: IProps): ReactElement => {
  const [open, setOpen] = useState<boolean>(false);
  const [tab, setTab] = useState(0);
  const darkMode = useSelector((state: IState) => state.darkMode);
  const [checked, setChecked] = useState<string[]>([]);
  const [textValue, setTextValue] = useState<string>(grades.join("; "));
  const { loading, data: steelGrades } = useFetch<ISteelGradeItem[]>( {
    module: "qa",
    query: "getSteelGradeCodes"
  });

  const onInternalChange = (value: string) => {
    const textList = value.replaceAll(",", ";").replaceAll(" ", ";");
    const tokens = textList.split(";").map(h => h.trim()).filter(h => h.length > 0);

    setTextValue(value);
    setChecked(tokens);
    onAccept(tokens);
  }

  const onClear = () => {
    setTextValue("");
    setChecked([]);
    onAccept([]);
  }

  const onInternalAccept = () => {
    setTextValue(checked.join("; "));
    setOpen(false);
    onAccept(checked);
  }

  const onOpenDialog = () => {
    const list = textValue.replaceAll(",", ";").replaceAll(" ", ";");
    const tokens = list.split(";").map(h => h.trim()).filter(h => h.length > 0);

    setChecked(tokens);
    setOpen(true);
  }

  const onCancel = () => {
    setOpen(false);
  }

  const handleTabChange = (_event: ChangeEvent<any>, newValue: number) => {
    setTab(newValue);
  };

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };


  // console.log("Render SteelSel", loading, steelGrades);


  return (
      <div>
        <TextField
            label="Steel Grades"
            value={textValue}
            onChange={e => onInternalChange(e.target.value)}
            sx={{ width: 500 }}
            slotProps={{
              input: {
                endAdornment:  <InputAdornment position="end">
                  <div className="display-flex-row">
                    <Tooltip title="Clear Selection">
                      <IconButton onClick={onClear} size="small">
                        <ClearIcon/>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open Selector">
                      <IconButton onClick={onOpenDialog} size="small">
                        <ChecklistIcon/>
                      </IconButton>
                    </Tooltip>
                  </div>
                </InputAdornment>
              }
            }}
            variant="standard"
        />
        <Dialog open={open} maxWidth={"md"} keepMounted={true}>
          <DialogTitle style={{ ...statusColor }}>
            Steel Grade Selector
          </DialogTitle>

          <DialogContent dividers={true} className="p-2" style={{ width: 400 }}>
            <Tabs
                value={tab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                centered={true}
                style={{  width: '100%', background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
              <Tab label="Last"/>
              <Tab label="All"/>
            </Tabs>

            { loading
                ? <div className="text-center"><CircularProgress /></div>
                : <List sx={{ width: '100%', maxHeight: 400,  bgcolor: 'background.paper', overflow: 'auto' }}>
              {(steelGrades ?? [])
                  .filter(s => tab === 0 ? s.isLast : !s.isLast)
                  .sort((a, b) => a.code.localeCompare(b.code))
                  .map((value, index) => {

                return (
                    <ListItem key={`steel-code-${index}`} disablePadding>
                      <ListItemButton role={undefined} onClick={handleToggle(value.code)} dense>
                        <ListItemIcon>
                          <Checkbox
                              edge="start"
                              checked={checked.includes(value.code)}
                              tabIndex={-1}
                              disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText  primary={value.code} />
                      </ListItemButton>
                    </ListItem>
                );
              })}
            </List>
            }
          </DialogContent>

          <DialogActions>
            <Button onClick={onInternalAccept} variant="contained" disabled={checked.length === 0} color="primary" startIcon={<CheckIcon />}>
              Accept
            </Button>
            <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon />}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
  );
};

export default SteelSelector;
